import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  getUserRole,
  formatAmountWithTwoDecimalPlaces,
  isLoginWithSupportPassword,
  isUserAdmin
} from 'shared/utils/function';
import { ROLE } from '../../../../shared/constants/common';

import '../message.css';
import { showError } from '../../../../shared/utils/swalHandler';
import { fetchRestrictedWords } from '../../../../shared/actions/matrixAction';
import ChatActions from './ChatActions';
import ChatHistoryStatus from './ChatHistoryStatus';
import GetNoticedEmptyState from './GetNoticedEmptyState';
import MessageHeader from '../../../../components/layout/messageHeader';
import PurchaseCreditsModal from '../../../Modals/Message/PurchaseCreditsModal';
import ChatCard from './card/ChatCard';
import ChatComposer from './ChatComposer';
import { CREDITS_DISPLAY_TEXT, WHALE_THRESHOLD } from '../../../../shared/constants/message';
import { DEFAULT_AVATAR } from '../../../../shared/utils/matrix/matrixRoomUtils';
import { MatrixContext } from '../../../../MatrixProvider';
import { MatrixCallContext } from '../../../../MatrixCallProvider';
import { useMatrixRoomInfo } from '../../../../shared/utils/matrix/useMatrixRoomInfo';
import { useMatrixRoomMessages } from '../../../../shared/utils/matrix/useMatrixRoomMessages';
import {
  buildThreadItems,
  isNearBottom,
  CenteredLoader,
  isCreatorMessagingOtherUser,
  shouldSkipPrioritySubscriberNotify,
} from '../../../../shared/utils/chatUtil';
import { useMessageRecipientNotify } from '../../../../shared/hooks/useMessageRecipientNotify';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { useChatHeaderSubtitle } from './hooks/useChatHeaderSubtitle';
import { useSendTextMessageWithTipReply } from './hooks/useSendTextMessageWithTipReply';
import { useHandleTipSuccessInChat } from './hooks/useHandleTipSuccessInChat';
import { useCreatorMessagingPurchasePolling } from './hooks/useCreatorMessagingPurchasePolling';
import { useMatrixOneToOneChatScroll } from './hooks/useMatrixOneToOneChatScroll';
import CallSummary from '../call/CallSummary';
import {
  useChatMobileViewport,
  useChatPageScrollLock,
} from './device/useChatPageViewport';

/**
 * Chatting page (1:1 thread).
 * Handles message rendering, date separators, sending, and backward pagination.
 */
const ChattingPage = ({
  restrictedWords
}) => {
  const { userId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const userRole = getUserRole();
  const isUser = userRole === ROLE.USER;
  const isCreator = userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
  const walletBalance = useSelector((state) => state.credit?.walletBalance);
  const { client, isInitialized, matrixUserInfo } = useContext(MatrixContext);
  const { incomingCall } = useContext(MatrixCallContext);

  const fromTab = location.state?.fromTab ?? null;
  const fromNewMessage = location.state?.fromNewMessage ?? false;
  const actionsDisabled = isLoginWithSupportPassword() || isUserAdmin();

  const handleBackFromChat = useCallback(() => {
    if (fromNewMessage) {
      // If user came from the New Message (subscribers) page, send them back there
      navigate.toNewMessage();
      return;
    }

    if (fromTab) {
      // Preserve the messages tab (all / priority / archived, etc.) when coming from messages list
      navigate.toMessages(fromTab);
      return;
    }

    // Fallback: use browser history when we don't know the origin
    navigate.toMessages('all');
  }, [navigate, fromNewMessage, fromTab]);

  const [creditsDisplayText, setCreditsDisplayText] = useState(CREDITS_DISPLAY_TEXT);
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  const [callSummaryPopupData, setCallSummaryPopupData] = useState(
    location.state?.callSummaryPopup || null
  );
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? Boolean(navigator.onLine) : true
  );
  const chatWrapperRef = useRef(null);
  const historyTopRef = useRef(null);
  const pageRef = useRef(null);
  const openTipModalRef = useRef({});
  const callActionRef = useRef({});

  const handleMissedCallBack = useCallback((kind) => {
    void callActionRef.current?.handleCallAction?.(kind);
  }, []);

  useEffect(() => {
    if (!location.state?.callSummaryPopup) return;
    setCallSummaryPopupData(location.state.callSummaryPopup);
  }, [location.state]);

  const handleCloseCallSummaryPopup = useCallback(() => {
    setCallSummaryPopupData(null);

    if (!location.state?.callSummaryPopup) return;

    const nextState = { ...(location.state || {}) };
    delete nextState.callSummaryPopup;
    history.replace(location.pathname + location.search, nextState);
  }, [history, location.pathname, location.search, location.state]);

  useEffect(() => {
    if (!incomingCall || !callSummaryPopupData) return;
    handleCloseCallSummaryPopup();
  }, [incomingCall, callSummaryPopupData, handleCloseCallSummaryPopup]);

  const {
    loading: loadingRoomInfo,
    roomId,
    hasPriority,
    isSubscribed,
    isCreatorReplyToTip,
    isAlreadyMessageTip,
    messagePurchaseContentIds,
    destUserInfo,
    error: roomInfoError,
    refresh: refreshRoomInfo,
  } = useMatrixRoomInfo(userId);

  useCreatorMessagingPurchasePolling({
    enabled: isCreator,
    userId,
    isRoomInfoReady: Boolean(roomId),
    refreshRoomInfo,
    client,
    roomId,
  });

  const myMatrixUserId = useMemo(
    () => client?.getUserId?.() || matrixUserInfo?.matrix_user_id || null,
    [client, matrixUserInfo?.matrix_user_id]
  );
  const isRoomInfoReady = Boolean(roomId);

  const otherDisplayName = useMemo(() => destUserInfo?.username || 'Unknown', [destUserInfo]);
  const otherAvatar = useMemo(() => destUserInfo?.profile_picture || DEFAULT_AVATAR, [destUserInfo]);

  const headerSubtitle = useChatHeaderSubtitle({
    loadingRoomInfo,
    hasPriority,
    isCreator,
    destUserInfo,
  });

  const showWhaleSubscriber = useMemo(
    () => isCreator && Number(destUserInfo?.totalSixtyDays) >= WHALE_THRESHOLD,
    [isCreator, destUserInfo?.totalSixtyDays]
  );

  useEffect(() => {
    if (walletBalance?.credits > 0) {
      setCreditsDisplayText(`Credits: ${formatAmountWithTwoDecimalPlaces(walletBalance?.credits)}`);
    } else {
      setCreditsDisplayText(CREDITS_DISPLAY_TEXT);
    }
  }, [walletBalance]);

  const {
    loading: loadingMessages,
    messages: roomMessages,
    sendLoading: isSending,
    sendTextMessage,
    sendEditMessage,
    paginateLoading,
    isAtConversationStart,
    hasMoreBackwards,
    loadOlderMessages,
    markAsReadManually,
    isReady: isChatReady,
  } = useMatrixRoomMessages({
    client,
    isInitialized,
    roomId,
    myMatrixUserId,
    otherDisplayName,
  });

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Mark room as read when tab is focused and user is at bottom (read receipt only when viewing latest).
  useEffect(() => {
    if (!isChatReady || !markAsReadManually) return;
    const onFocus = () => {
      const container = chatWrapperRef.current;
      if (container && isNearBottom(container)) markAsReadManually();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isChatReady, markAsReadManually]);

  // Fetch restricted words if not already loaded
  useEffect(() => {
    if (!restrictedWords || restrictedWords.length === 0) {
      dispatch(fetchRestrictedWords());
    }
  }, [dispatch, restrictedWords]);

  // Track the last message key for scroll position detection
  // (length can stay constant even as new messages arrive)
  const lastVisibleMessageKey = useMemo(() => {
    if (!roomMessages.length) return null;
    const last = roomMessages[roomMessages.length - 1];
    return last?.eventId || last?.id || last?.ts || null;
  }, [roomMessages]);

  // Render list with WhatsApp-style date separators
  const threadItems = useMemo(() => buildThreadItems(roomMessages), [roomMessages]);
  const isCreatorMessagingOtherUserInThread = isCreatorMessagingOtherUser(isCreator, threadItems);

  const isMessageListPainted = useMemo(
    () =>
      !loadingRoomInfo &&
      isRoomInfoReady &&
      isChatReady &&
      !loadingMessages &&
      roomMessages.length > 0,
    [loadingRoomInfo, isRoomInfoReady, isChatReady, loadingMessages, roomMessages.length]
  );

  // Defer scroll/pagination hooks until the thread branch has committed and laid out.
  // Same render sets loadingMessages=false and paints rows, but flex/media height is
  // often wrong on the first layout pass — scrolling then sticks at the top (old rows).
  const [isMessageListLayoutReady, setIsMessageListLayoutReady] = useState(false);

  useLayoutEffect(() => {
    if (!isMessageListPainted) {
      setIsMessageListLayoutReady(false);
      return undefined;
    }

    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setIsMessageListLayoutReady(true);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [isMessageListPainted, roomId]);

  const isScrollReady = isMessageListPainted && isMessageListLayoutReady;

  const { handleChatScroll, scrollToBottom, forceScrollToBottomRef, isLoadingOlder } =
    useMatrixOneToOneChatScroll({
    chatWrapperRef,
    historyTopRef,
    roomId,
    roomMessages,
    lastVisibleMessageKey,
    loadingMessages,
    isChatReady,
    isMessageListPainted: isScrollReady,
    loadingOlder: paginateLoading,
    hasMoreBackwards,
    loadOlderMessages,
    markAsReadManually,
  });

  // Lock and viewport for mobile devices
  const isGetNoticedEmpty = roomMessages.length === 0 && !isAlreadyMessageTip && isUser;
  useChatPageScrollLock(pageRef, chatWrapperRef);
  useChatMobileViewport({
    pageRef,
    scrollContainerRef: chatWrapperRef,
    scrollToBottom,
    disableViewportAdjust: isGetNoticedEmpty,
  });

  // Handle room info errors
  useEffect(() => {
    if (roomInfoError) {
      showError('Error', roomInfoError);
    }
  }, [roomInfoError]);

  /** Matrix send, then REST notify (email/push): creators always; subscribers only when priority. */
  const notifyMessageRecipientInBackground = useMessageRecipientNotify({
    recipientUserId: destUserInfo?._id || userId,
    senderUserRole: userRole,
    receiverUserRole: destUserInfo?.rol,
    isPriority: hasPriority,
  });

  const sendNotifyToUser = useCallback(
    async (text) => {
      const result = await sendTextMessage(text);

      const trimmed = typeof text === 'string' ? text.trim() : '';
      // Creators: always notify. Subscribers: only when priority for that creator.
      const shouldNotifyRecipient = isCreator || (isUser && hasPriority);
      if (
        result?.success &&
        trimmed &&
        shouldNotifyRecipient &&
        !shouldSkipPrioritySubscriberNotify(text)
      ) {
        notifyMessageRecipientInBackground(trimmed);
      }
      return result;
    },
    [
      sendTextMessage,
      notifyMessageRecipientInBackground,
      hasPriority,
      isCreator,
      isUser,
    ]
  );

  const handleTipSuccessInChat = useHandleTipSuccessInChat({
    sendTextMessage: isUser ? sendNotifyToUser : sendTextMessage,
    scrollToBottom,
    openTipModalRef,
    forceScrollToBottomRef,
  });

  const handleUploadContentSuccessInChat = useCallback(
    (matrixPayload) => {
      if (!sendTextMessage || !matrixPayload) return;

      const serialized = JSON.stringify(matrixPayload);

      sendTextMessage(serialized).then(() => {
        forceScrollToBottomRef.current = true;
        scrollToBottom('smooth');
      });
    },
    [sendTextMessage, scrollToBottom, forceScrollToBottomRef]
  );

  // Lock only for subscribers: they must be subscribed to send. Creators can always message (including archived/locked chats).
  const canSubscriberSendMessages = !isUser || isSubscribed;

  const sendTextMessageWithTipReply = useSendTextMessageWithTipReply({
    sendTextMessage,
    isCreator,
    threadItems,
    destUserInfo,
    refreshRoomInfo,
  });

  const refreshRoomInfoWithoutRouteChange = useCallback(
    async (options) => {
      const routeBefore = {
        pathname: history.location.pathname,
        search: history.location.search,
        hash: history.location.hash,
      };

      const result = await refreshRoomInfo(options);

      const routeChanged =
        history.location.pathname !== routeBefore.pathname ||
        history.location.search !== routeBefore.search ||
        history.location.hash !== routeBefore.hash;

      if (routeChanged) {
        history.replace(routeBefore);
      }

      return result;
    },
    [refreshRoomInfo, history]
  );

  const isEmptyChat = roomMessages.length === 0;
  return (
    <div ref={pageRef} className="modern-message-page modern-chat-page">
      <MessageHeader
        {...isUser && (
          { profileUsername: loadingRoomInfo ? 'Loading...' : otherDisplayName || '' }
        )}
        title={loadingRoomInfo ? 'Loading...' : otherDisplayName || ''}
        imgSrc={otherAvatar}
        subtitle={headerSubtitle}
        showPriority={hasPriority}
        showWhaleSubscriber={showWhaleSubscriber}
        showCredits={isUser}
        creditsText={creditsDisplayText}
        onCreditsClick={() => setShowPurchaseCreditsModal(true)}
        showBackButton={true}
        onBackClick={handleBackFromChat}
      />

      <main className="modern-contact-main" id="modern_contact_main">
        <div
          id='cm_chat_wrapper'
          className={`chat_wrapper${isEmptyChat && !isAlreadyMessageTip && isUser ? ' chat_wrapper_empty' : ''}${isUser ? ' cm_subscriber_chat_timestamps' : ''}`}
          ref={chatWrapperRef}
          onScroll={handleChatScroll}
        >
          {loadingRoomInfo ? (
            <CenteredLoader size={44} text="Loading chat..." />
          ) : !isRoomInfoReady ? (
            <div className="text-center p-4">No conversation found.</div>
          ) : !isChatReady ? (
            <CenteredLoader text="Connecting to chat..." />
          ) : loadingMessages ? (
            <CenteredLoader text="Loading messages..." />
          ) : isEmptyChat && !isAlreadyMessageTip ? (
            isUser && !actionsDisabled ? (
              <GetNoticedEmptyState
                creatorInfo={destUserInfo}
                sendTextMessage={sendNotifyToUser}
                openTipModalRef={openTipModalRef}
                restrictedWords={restrictedWords}
                isOnline={isOnline}
                isSending={isSending}
                canSendMessages={canSubscriberSendMessages}
              />
            ) : (
              <div className="text-center p-4">No messages yet.</div>
            )
          ) : (
            <>
              {threadItems.length <= 4 ? <div className='for_cm_height'></div> : ''}

              <ChatHistoryStatus
                ref={historyTopRef}
                isLoadingOlder={Boolean(isLoadingOlder)}
                isAtConversationStart={Boolean(isAtConversationStart)}
              />

              {threadItems.map((row) =>
                row.type === 'date' ? (
                  <div key={row.key} className="cm_date_separator" role="separator" aria-label={row.label}>
                    <span className="cm_date_separator_chip">{row.label}</span>
                  </div>
                ) : (
                  <ChatCard
                    key={row.key}
                    item={row.item}
                    otherAvatar={otherAvatar}
                    isUser={isUser}
                    hasPriority={hasPriority}
                    isCreatorReplyToTip={isCreatorReplyToTip}
                    creatorInfo={destUserInfo}
                    messagePurchaseContentIds={messagePurchaseContentIds}
                    onEditMessage={sendEditMessage}
                    roomId={roomId}
                    onCallBack={handleMissedCallBack}
                  />
                )
              )}
            </>
          )}
        </div>

        {isRoomInfoReady && !actionsDisabled && (
          <div
            className="chat_editer_wrapper"
            style={{
              display: isUser && isEmptyChat && !isAlreadyMessageTip ? 'none' : undefined,
            }}
          >
            <ChatActions
              userRole={userRole}
              roomId={roomId}
              otherUserId={destUserInfo?._id}
              creatorInfo={destUserInfo}
              onTipSuccessInChat={handleTipSuccessInChat}
              isEmptyChat={isEmptyChat && !isAlreadyMessageTip}
              openTipModalRef={openTipModalRef}
              callActionRef={callActionRef}
              onUploadSuccessInChat={handleUploadContentSuccessInChat}
              isSubscribed={isSubscribed}
              onRefreshRoomInfo={refreshRoomInfoWithoutRouteChange}
            />

            <ChatComposer
              roomId={roomId}
              creatorInfo={destUserInfo}
              isOnline={isOnline}
              isRoomInfoReady={isRoomInfoReady}
              isChatReady={isChatReady}
              isSending={isSending}
              canSendMessages={canSubscriberSendMessages}
              sendTextMessage={
                isAlreadyMessageTip || isCreatorMessagingOtherUserInThread
                  ? sendNotifyToUser
                  : sendTextMessageWithTipReply
              }
              scrollToBottom={scrollToBottom}
              forceScrollToBottomRef={forceScrollToBottomRef}
              restrictedWords={restrictedWords}
              openTipModalRef={openTipModalRef}
            />
          </div>
        )}
      </main>

      <PurchaseCreditsModal
        isOpen={showPurchaseCreditsModal}
        onClose={() => setShowPurchaseCreditsModal(false)}
      />

      {callSummaryPopupData && (
        <CallSummary
          embedded
          summaryOverride={callSummaryPopupData}
          onClose={handleCloseCallSummaryPopup}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  restrictedWords: state.matrix?.restrictedWords?.data
});

export default connect(mapStateToProps)(React.memo(ChattingPage));